/**
 * QA / automation hooks. Exposed as window.__EVO__ in every build (cheap, and the
 * QA agent tests production deployments too).
 *
 * URL params:
 *   ?stage=<id>        start a new run directly at that stage (skips title)
 *   &autopilot=1       stage bot plays by itself
 *   &path=<key>        autopilot / affinity leans toward this branch key
 *   &god=1             player can't die
 *   &timescale=<n>     simulation speed multiplier (1..8)
 *   &quality=low|medium|high
 *   &mute=1            silence audio
 *   &skipcard=1        skip stage title card
 *   &fps=1             show FPS counter
 */
import type { Game } from './Game';
import { ALL_STAGES, TREE, isStageId, type StageId } from './tree';

export interface EvoDebugApi {
  version: string;
  game: Game;
  state(): Record<string, unknown>;
  start(stage?: StageId, opts?: { affinity?: Record<string, number>; traits?: string[] }): Promise<void>;
  setTimeScale(n: number): void;
  setGod(on: boolean): void;
  autopilot(on: boolean, preferPath?: string): void;
  completeStage(pathKey?: string): void;
  setGrowth(frac: number): void;
  addAffinity(key: string, amount: number): void;
  kill(): void;
  pause(): void;
  resume(): void;
  command(cmd: string, arg?: unknown): unknown;
  events(sinceIndex?: number): Array<{ t: number; name: string; payload: unknown }>;
  errors: string[];
  stages: StageId[];
}

declare global {
  interface Window {
    __EVO__?: EvoDebugApi;
  }
}

export function installDebug(game: Game): EvoDebugApi {
  const errors: string[] = [];
  window.addEventListener('error', (e) => errors.push(`${e.message} @ ${e.filename}:${e.lineno}`));
  window.addEventListener('unhandledrejection', (e) => errors.push(`unhandledrejection: ${String(e.reason?.stack ?? e.reason)}`));
  game.events.on('error', ({ message }) => errors.push(message));

  const api: EvoDebugApi = {
    version: __EVO_VERSION__,
    game,
    stages: ALL_STAGES,
    errors,
    state() {
      const stage = game.stage;
      const ctx = (game as any).ctx;
      return {
        screen: game.screen,
        stage: game.run.stage,
        stageName: TREE[game.run.stage]?.name,
        growth: ctx?.growth ?? 0,
        stageTime: ctx?.time ?? 0,
        playTime: game.run.data.playTime,
        affinity: { ...game.run.data.affinity },
        traits: [...game.run.data.traits],
        history: [...game.run.data.history],
        devolutions: game.run.data.devolutions,
        fps: Math.round(game.renderSystem.fps),
        pixelRatio: game.renderSystem.pixelRatio,
        quality: game.quality.tier,
        device: game.quality.device,
        backend: game.renderSystem.backend,
        timeScale: game.timeScale,
        god: game.debug.god,
        autopilot: game.debug.autopilot,
        endings: [...game.profile.data.endings],
        stageDebug: stage ? safe(() => stage.debugState()) : undefined,
        errors: errors.length,
      };
    },
    async start(stage = 'cell', opts = {}) {
      await game.newRun(stage, { ...opts, skipCard: true });
    },
    setTimeScale(n) {
      game.timeScale = Math.max(0.1, Math.min(8, n));
    },
    setGod(on) {
      game.debug.god = on;
    },
    autopilot(on, preferPath) {
      game.debug.autopilot = on;
      game.debug.preferPath = preferPath;
      game.stage?.setAutopilot(on, preferPath);
    },
    completeStage(pathKey) {
      const ctx = (game as any).ctx;
      if (ctx) ctx.complete(pathKey);
    },
    setGrowth(frac) {
      (game as any).ctx?.setGrowth(frac);
    },
    addAffinity(key, amount) {
      (game as any).ctx?.addAffinity(key, amount);
    },
    kill() {
      const was = game.debug.god;
      game.debug.god = false;
      (game as any).ctx?.playerDied('debug');
      game.debug.god = was;
    },
    pause: () => game.pause(),
    resume: () => game.resume(),
    command(cmd, arg) {
      return game.stage?.debugCommand(cmd, arg);
    },
    events(sinceIndex = 0) {
      return game.events.log.slice(sinceIndex);
    },
  };
  window.__EVO__ = api;
  return api;
}

function safe<T>(fn: () => T): T | { error: string } {
  try {
    return fn();
  } catch (err) {
    return { error: String(err) };
  }
}

/** Apply URL params. Returns true if it started a stage directly (skip title). */
export async function applyUrlParams(game: Game, api: EvoDebugApi): Promise<boolean> {
  const q = new URLSearchParams(location.search);
  if (q.get('god') === '1') game.debug.god = true;
  if (q.get('verbose') === '1') game.debug.verbose = true;
  const ts = Number(q.get('timescale'));
  if (ts > 0) api.setTimeScale(ts);
  const path = q.get('path') ?? undefined;
  if (q.get('autopilot') === '1') {
    game.debug.autopilot = true;
    game.debug.preferPath = path;
  }
  if (q.get('mute') === '1') game.audio.setVolumes({ master: 0, music: 0, sfx: 0, voice: 0 });
  if (q.get('fps') === '1') game.ui.showFps(true);
  if (q.get('resume') === '1' && game.hasSave()) {
    // Came back from a reload onto a new build (#135): continue the saved run.
    const u = new URL(location.href);
    u.searchParams.delete('resume');
    history.replaceState(null, '', u.toString());
    await game.continueRun();
    return true;
  }
  const stage = q.get('stage');
  if (stage && isStageId(stage)) {
    const affinity = path ? { [path]: 50 } : undefined;
    await game.newRun(stage, { affinity, skipCard: q.get('skipcard') === '1' });
    return true;
  }
  return false;
}
